<template>
    <section class="pl-showcase pl-showcase--cast">
        <!-- The campaign you just walked into, still on the wall behind you.
             The lobby's tavern belongs to the step where no campaign has been
             chosen yet; here the room is this campaign's own. -->
        <div class="pl-showcase__plates" aria-hidden="true">
            <div class="pl-showcase__plate is-on" :style="bannerStyle(campaign)"></div>
            <div class="pl-showcase__veil is-lit"></div>
        </div>

        <div class="pl-showcase__copy">
            <template v-if="selected">
                <div class="pl-showcase__head">
                    <img
                        v-if="pathIcon"
                        :src="pathIcon"
                        class="pl-showcase__sigil"
                        alt=""
                        loading="lazy"
                        decoding="async"
                    />
                    <p class="pl-showcase__eyebrow">{{ pathName }}</p>
                </div>
                <h1 class="pl-showcase__title">
                    <ConsoleText :text="displayName" :speed="26" @done="nameWritten = true" />
                </h1>

                <ChipList class="pl-showcase__meta">
                    <li v-if="selected.rank" class="pl-chip">{{ selected.rank.name }}</li>
                    <li class="pl-chip pl-chip--muted">
                        <span class="pl-num">{{ selected.experience ?? 0 }}</span>
                        {{ t('player.detail.experience') }}
                    </li>
                    <li v-if="incomplete" class="pl-chip pl-chip--danger">
                        {{ t('player.lobby.card.needsFinishing') }}
                    </li>
                    <li v-if="selected.fight" class="pl-chip pl-chip--info">
                        {{ t('player.lobby.card.inCombat') }}
                    </li>
                    <li v-if="publicationState === 'published'" class="pl-chip pl-chip--success">
                        {{ t('player.lobby.card.published') }}
                    </li>
                    <li
                        v-if="publicationState === 'pending' || publicationState === 'edit-pending'"
                        class="pl-chip pl-chip--info"
                    >
                        {{ t('player.lobby.card.inReview') }}
                    </li>
                </ChipList>

                <p class="pl-showcase__lede" :class="{ 'pl-showcase__lede--empty': !background }">
                    <ConsoleText
                        :text="background || t('player.lobby.card.noBiography')"
                        :speed="14"
                        :active="nameWritten"
                    />
                </p>

                <div class="pl-showcase__actions">
                    <router-link
                        v-if="incomplete"
                        class="pl-btn pl-btn--primary"
                        :to="{ name: 'PlayerCharacterEdit', params: { characterId: selected.id } }"
                    >{{ t('player.lobby.card.finish') }}
                    </router-link>
                    <button
                        v-else
                        type="button"
                        class="pl-btn pl-btn--primary"
                        :aria-busy="entering"
                        :disabled="entering"
                        @click="$emit('enter', selected.id)"
                    >
                        <span v-if="entering" class="dx-loader dx-loader--xs" aria-hidden="true"></span>
                        {{ t('player.lobby.card.enter') }}
                    </button>
                    <router-link
                        class="pl-btn pl-btn--ghost"
                        :to="{ name: 'PlayerCharacterDetail', params: { characterId: selected.id } }"
                    >{{ t('player.lobby.card.review') }}
                    </router-link>
                    <button
                        v-if="!incomplete && trainingEnabled"
                        type="button"
                        class="pl-btn pl-btn--ghost"
                        :aria-busy="training"
                        :disabled="training || endingTraining"
                        @click="$emit('train', selected.id)"
                    >
                        <span v-if="training" class="dx-loader dx-loader--xs" aria-hidden="true"></span>
                        {{ trainingActive ? t('player.lobby.card.continueTraining') : t('player.lobby.card.train') }}
                    </button>
                    <button
                        v-if="!incomplete && trainingEnabled && trainingActive"
                        type="button"
                        class="pl-btn pl-btn--ghost"
                        :aria-busy="endingTraining"
                        :disabled="training || endingTraining"
                        @click="$emit('end-training', selected.id)"
                    >
                        <span v-if="endingTraining" class="dx-loader dx-loader--xs" aria-hidden="true"></span>
                        {{ t('player.lobby.card.endTraining') }}
                    </button>
                </div>
            </template>
        </div>

        <!-- The character themself, standing in the room at something near
             human size. It is a plate rather than a framed avatar: the figure
             dissolves into the scene on the side the caption comes from. -->
        <div v-if="selected" class="pl-cast__stand" aria-hidden="true">
            <div class="pl-cast__plate">
                <CharacterPortrait
                    :character-id="selected.id"
                    :name="displayName"
                    :avatar="avatarOf(selected)"
                    :distinguishing="isDistinguishing(selected)"
                    size="hero"
                />
            </div>
        </div>

        <LobbyStrip
            class="pl-strip--cast"
            :items="slots"
            :selected-id="selectedId"
            :label="seatLabel"
            :aria-label="t('player.lobby.rosterCaption')"
            :loading="loading"
            @select="onSelect"
        >
            <template #default="{ item }">
                <template v-if="item.add">
                    <span class="pl-mini__art pl-mini__art--recruit" aria-hidden="true"></span>
                    <span class="pl-mini__add">
                        <i class="fas fa-plus" aria-hidden="true"></i>
                    </span>
                    <span class="pl-mini__name">{{ t('player.lobby.addCharacter') }}</span>
                </template>
                <template v-else>
                    <span class="pl-mini__art pl-mini__art--portrait" aria-hidden="true">
                        <CharacterPortrait
                            :character-id="item.id"
                            :name="nameOf(item)"
                            :avatar="avatarOf(item)"
                            :distinguishing="isDistinguishing(item)"
                            size="seat"
                        />
                    </span>
                    <span class="pl-mini__name">{{ nameOf(item) }}</span>
                </template>
            </template>
        </LobbyStrip>
    </section>
</template>

<script setup>
import {computed, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import LobbyStrip from '@/components/Player/LobbyStrip.vue';
import CharacterPortrait from '@/components/Player/CharacterPortrait.vue';
import ConsoleText from '@/components/Player/ConsoleText.vue';
import ChipList from '@/components/Player/ChipList.vue';
import {bannerStyle} from '@/components/Player/campaignBanner.js';
import {
    duplicatedAvatars,
    hasOwnAvatar,
    hasUnusableName,
    isBlankText,
    isIncomplete,
    publicationStateOf,
} from '@/components/Player/characterPresentation.js';
import {TRAINING_ENABLED} from '@/config/features.js';

/* The trailing card on the strip. An id rather than a flag on the strip's
   contract: the ring places whatever it is given, and only this component
   knows that one of its cards is a door rather than a person. */
const ADD_ID = '__add__';

const props = defineProps({
    campaign: {type: Object, default: null},
    characters: {type: Array, default: () => []},
    selectedId: {type: String, default: null},
    entering: {type: Boolean, default: false},
    training: {type: Boolean, default: false},
    endingTraining: {type: Boolean, default: false},
    trainingActive: {type: Boolean, default: false},
    loading: {type: Boolean, default: false},
});

const emit = defineEmits(['select', 'enter', 'train', 'end-training', 'create']);

const {t} = useI18n();

const trainingEnabled = TRAINING_ENABLED;

/* The caption is written top to bottom: the name first, then the line under
   it. Reset the moment the character changes, or the second line would still
   be finishing the last one's story. */
const nameWritten = ref(false);
watch(() => props.selectedId, () => {
    nameWritten.value = false;
});

const selected = computed(
    () => props.characters.find((character) => character.id === props.selectedId) ?? null,
);

const slots = computed(() => [...props.characters, {id: ADD_ID, add: true}]);

/* Two characters wearing the same stock portrait are not told apart by it, so
   neither is given the treatment that says "this face is mine". */
const sharedAvatars = computed(() => duplicatedAvatars(props.characters));

function avatarOf(character) {
    return character.biography?.avatar ?? '';
}

function nameOf(character) {
    return hasUnusableName(character.name) ? t('player.lobby.card.unfinished') : character.name;
}

function isDistinguishing(character) {
    const avatar = avatarOf(character);
    if (!hasOwnAvatar(avatar)) return false;
    return !sharedAvatars.value.has(avatar);
}

function seatLabel(item) {
    return item.add ? t('player.lobby.addCharacter') : nameOf(item);
}

function onSelect(id) {
    if (id === ADD_ID) emit('create');
    else emit('select', id);
}

const displayName = computed(() => (selected.value ? nameOf(selected.value) : ''));
const pathName = computed(() => selected.value?.path?.name ?? t('player.lobby.card.noPath'));
const pathIcon = computed(() => selected.value?.path?.icon ?? '');
const incomplete = computed(() => !!selected.value && isIncomplete(selected.value));
const publicationState = computed(
    () => (selected.value ? publicationStateOf(selected.value) : null),
);
const background = computed(() => {
    const text = selected.value?.biography?.background;
    return isBlankText(text) ? '' : text;
});
</script>

<style scoped>
</style>
